/**
 * Leonardo Constants & Configuration
 * 
 * Single source of truth for:
 * - Contrast ratio ladders (light / dark appearance)
 * - Color definitions
 * - Default configurations
 */

import type { CssColor } from '@adobe/leonardo-contrast-colors';
import type { ColorScale, ThemeConfig } from './types';

// =============================================================================
// CONTRAST RATIOS
// =============================================================================

/**
 * Contrast ratio ladder for light appearance (foreground on light background).
 * Tune independently from {@link STANDARD_RATIOS_DARK}; keep the same length
 * so Leonardo step indices stay aligned with CSS variables.
 */
export const STANDARD_RATIOS_LIGHT = [
  1.00,  // No contrast
  1.15,  // Subtle contrast
  1.21,  // Very light
  2.32,  // Light
  3.12,  // Readable
  4.96,  // Good contrast (close to WCAG AA for large text)
  7.05,  // Strong contrast (exceeds WCAG AA)
  9.85,  // Very strong
  16.48, // Maximum
  18.75, // Extreme
];

/**
 * Contrast ratio ladder for dark appearance (foreground on dark background).
 * Starts identical to light; adjust per mode for perceptual balance while keeping length.
 */
export const STANDARD_RATIOS_DARK = [
  1.00,  // No contrast
  1.08,  // Subtle contrast
  1.21,  // Very light
  2.67,  // Light
  3.42,  // Readable
  4.99,  // Good contrast (close to WCAG AA for large text)
  7.05,  // Strong contrast (exceeds WCAG AA)
  9.84,  // Very strong
  16.48, // Maximum
  18.75, // Extreme
];

/**
 * Legacy default ladder (same values as light mode).
 * Prefer {@link STANDARD_RATIOS_LIGHT} / {@link STANDARD_RATIOS_DARK} or {@link getDefaultRatiosForMode}.
 */
export const STANDARD_RATIOS = STANDARD_RATIOS_LIGHT;

/**
 * Default contrast ratios for the current color appearance mode.
 */
export const getDefaultRatiosForMode = (mode: 'light' | 'dark'): number[] =>
  mode === 'dark' ? [...STANDARD_RATIOS_DARK] : [...STANDARD_RATIOS_LIGHT];

/**
 * Optional ratio overrides for {@link createTheme} (runtime config).
 */
export interface RatioOverrideConfig {
  ratios?: number[];
  ratiosLight?: number[];
  ratiosDark?: number[];
}

/**
 * Resolves the contrast ratio ladder for {@link createTheme}.
 * Order: legacy `ratios` → mode-specific override → {@link getDefaultRatiosForMode}.
 */
export const resolveRatiosForTheme = (
  config: RatioOverrideConfig,
  colorMode: 'light' | 'dark'
): number[] => {
  if (config.ratios != null && config.ratios.length > 0) {
    return [...config.ratios];
  }
  if (colorMode === 'light' && config.ratiosLight != null && config.ratiosLight.length > 0) {
    return [...config.ratiosLight];
  }
  if (colorMode === 'dark' && config.ratiosDark != null && config.ratiosDark.length > 0) {
    return [...config.ratiosDark];
  }
  return getDefaultRatiosForMode(colorMode);
};

/**
 * Demo contrast ratios for plugin mockup (5 WCAG ratios)
 * Used specifically for the interactive demo
 */
export const DEMO_CONTRAST_RATIOS = [
  1.54,  // Light
  2.27,  // Readable
  3.88,  // Good contrast (close to WCAG AA for large text)
  5.40,  // Strong contrast (exceeds WCAG AA)
  10.69, // Very strong
];

/**
 * Demo APCA Lc values (equivalent to DEMO_CONTRAST_RATIOS)
 * These are approximate APCA equivalents for WCAG [1.54, 2.27, 3.88, 5.40, 10.69]
 * Used when the demo is in APCA mode
 */
export const DEMO_APCA_VALUES = [
  30,   // ~WCAG 1.54
  45,   // ~WCAG 2.27
  60,   // ~WCAG 3.88 (AA for large text)
  75,   // ~WCAG 5.40 (AA for body text)
  90,   // ~WCAG 10.69 (AAA for fluent text)
];

/**
 * APCA Lc value ranges
 * APCA can return values from -108 to +106, but we focus on positive values
 * for the interactive demo (matching the theme's lightness direction)
 */
export const APCA_MIN = 1;   // Minimum practical Lc for UI purposes
export const APCA_MAX = 108;  // Maximum Lc value

// =============================================================================
// COLORSPACE & INTERPOLATION
// =============================================================================

/**
 * Standard colorspace for all scales
 * OKLCH provides perceptually uniform color interpolation
 */
export const STANDARD_COLORSPACE = 'OKLCH' as const;

/**
 * Smooth interpolation setting
 * false = use Leonardo's default interpolation
 */
export const STANDARD_SMOOTH = false as const;

// =============================================================================
// BASE COLORS (Single Source of Truth)
// =============================================================================

/**
 * Base color definitions - SINGLE SOURCE OF TRUTH
 * 
 * These are the source colors that Leonardo uses to generate all scales.
 * These values are ALWAYS injected as CSS variables (--base-*) at runtime.
 * 
 * SCSS values in _foundation.scss are ONLY defaults before JavaScript loads.
 * All dynamic color updates flow through this constant → theme → CSS injection.
 * 
 * To change base colors:
 * 1. Update these values OR
 * 2. Pass custom baseColors to theme config (for dynamic/random palettes)
 */
export const BASE_COLORS = {
  black: '#3C3A43',
  red: '#FF6038',
  orange: '#FFA411',
  blue: '#2E8CFF',
} as const;

/**
 * Background colors for different modes
 */
export const BACKGROUND_COLORS = {
  dark: '#000000',
  light: '#FFFFFF',
} as const;

// =============================================================================
// COLOR SCALE FACTORY
// =============================================================================

/**
 * Helper to create a color configuration with standard settings
 */
export const createColorConfig = (
  name: string,
  colorKeys: string[],
  ratios = STANDARD_RATIOS_LIGHT,
  colorspace = STANDARD_COLORSPACE,
  smooth = STANDARD_SMOOTH
): ColorScale => ({
  name,
  colorKeys: colorKeys as CssColor[], // Cast for Leonardo compatibility
  colorspace,
  ratios: [...ratios], // Clone to avoid mutation
  smooth,
});

// =============================================================================
// DEFAULT THEME CONFIGURATIONS
// =============================================================================

/**
 * Default theme configuration for interactive demo
 * Background: Black (#000000)
 * 
 * Color names will be converted to kebab-case for CSS variables:
 * - "Black" → --color-black-{1-9} (from BASE_COLORS.black #3C3A43)
 * - "Red" → --color-red-{1-9}
 * - "Orange" → --color-orange-{1-9}
 * - "Blue" → --color-blue-{1-9}
 */
export const DEFAULT_THEME_CONFIG: ThemeConfig = {
  backgroundColor: createColorConfig('Background', [BACKGROUND_COLORS.dark], STANDARD_RATIOS_DARK),
  colors: [
    createColorConfig('Black', [BASE_COLORS.black], STANDARD_RATIOS_DARK),
    createColorConfig('Red', [BASE_COLORS.red], STANDARD_RATIOS_DARK),
    createColorConfig('Orange', [BASE_COLORS.orange], STANDARD_RATIOS_DARK),
    createColorConfig('Blue', [BASE_COLORS.blue], STANDARD_RATIOS_DARK),
  ],
  lightness: 100,
  contrast: 1,
  saturation: 100,
};

/**
 * Light mode configuration for static scales
 * Background: White (#FFFFFF)
 * 
 * Note: "Black" scale uses BASE_COLORS.black (#3C3A43), not pure black
 */
export const LIGHT_MODE_CONFIG: ThemeConfig = {
  backgroundColor: createColorConfig('White', [BACKGROUND_COLORS.light], STANDARD_RATIOS_LIGHT),
  colors: [
    createColorConfig('Black', [BASE_COLORS.black], STANDARD_RATIOS_LIGHT),
    createColorConfig('Red', [BASE_COLORS.red], STANDARD_RATIOS_LIGHT),
    createColorConfig('Orange', [BASE_COLORS.orange], STANDARD_RATIOS_LIGHT),
    createColorConfig('Blue', [BASE_COLORS.blue], STANDARD_RATIOS_LIGHT),
  ],
  lightness: 100,
  contrast: 1,
  saturation: 100,
};

/**
 * Dark mode configuration for static scales
 * Background: Black (#000000)
 * 
 * Note: "Black" scale uses BASE_COLORS.black (#3C3A43), not pure black
 */
export const DARK_MODE_CONFIG: ThemeConfig = {
  backgroundColor: createColorConfig('Background', [BACKGROUND_COLORS.dark], STANDARD_RATIOS_DARK),
  colors: [
    createColorConfig('Black', [BASE_COLORS.black], STANDARD_RATIOS_DARK),
    createColorConfig('Red', [BASE_COLORS.red], STANDARD_RATIOS_DARK),
    createColorConfig('Orange', [BASE_COLORS.orange], STANDARD_RATIOS_DARK),
    createColorConfig('Blue', [BASE_COLORS.blue], STANDARD_RATIOS_DARK),
  ],
  lightness: 0,
  contrast: 1,
  saturation: 100,
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Round contrast ratio to 2 decimal places
 */
export const roundContrast = (value: number): number => {
  return Math.round(value * 100) / 100;
};

